import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronRight, ChevronDown, Plus, Search, LayoutGrid, X,
  Folder as FolderIcon, Cog, Users as UsersIcon, Briefcase, Building2, Layers,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { StatusDot } from "@/components/Badges";
import { KebabMenu } from "@/components/KebabMenu";
import { Tip } from "@/components/ui/tooltip";

const ICON_MAP = { folder: FolderIcon, cog: Cog, users: UsersIcon, briefcase: Briefcase, building: Building2, layers: Layers };
export function VerticalIcon({ name = "folder", size = 14, className = "" }) {
  const Comp = ICON_MAP[name] || FolderIcon;
  return <Comp size={size} className={className} />;
}
export { ICON_MAP };

// ProjectsSidebar — lives inside Layout's secondary nav on /projects.
// - URL params drive selection: ?focus=<projectId>&scope=<all|vertical:id|category:id|project:id>
// - Clicking a row updates BOTH so the tree tab and kanban tab stay in sync.
export default function ProjectsSidebar() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { tree, reload } = useWorkspace();
  const focus = params.get("focus") || "";
  const scope = params.get("scope") || "all";
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [modal, setModal] = useState(null);

  // All Business Vertical accordions collapsed by default (user preference).
  // Only auto-expand the vertical containing a focused project.
  useEffect(() => {
    if (!focus) return;
    const p = tree.projects.find((x) => x.id === focus);
    if (!p) return;
    setExpanded((prev) => {
      if (prev.has(p.folder_id)) return prev;
      const n = new Set(prev); n.add(p.folder_id); return n;
    });
  }, [focus, tree.projects]);

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tree.projects;
    return tree.projects.filter((p) => p.name.toLowerCase().includes(q));
  }, [search, tree.projects]);

  const toggleFolder = (id) => {
    const n = new Set(expanded); n.has(id) ? n.delete(id) : n.add(id); setExpanded(n);
  };

  const goto = (params) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) { if (v) sp.set(k, v); }
    nav(`/projects?${sp.toString()}`);
  };

  const dragDisabled = !!search.trim();
  const handleDragEnd = async (result) => {
    const { source, destination, type } = result;
    if (!destination || source.droppableId !== destination.droppableId || source.index === destination.index) return;
    if (type === "FOLDER") {
      const arr = Array.from(tree.folders); const [m] = arr.splice(source.index, 1); arr.splice(destination.index, 0, m);
      await endpoints.reorder("folders", arr.map((f) => f.id)); reload(); return;
    }
    const containerProjects = (dropId) => {
      if (dropId.startsWith("proj-group-")) {
        const gid = dropId.replace("proj-group-", "");
        return tree.projects.filter((p) => p.group_id === gid);
      }
      const fid = dropId.replace("proj-folder-", "");
      return tree.projects.filter((p) => p.folder_id === fid && !p.group_id);
    };
    const arr = Array.from(containerProjects(source.droppableId));
    const [m] = arr.splice(source.index, 1); arr.splice(destination.index, 0, m);
    await endpoints.reorder("projects", arr.map((p) => p.id)); reload();
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="p-2 shrink-0">
        <div className="flex items-center gap-1.5">
          <div className="flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-neutral-200 bg-white">
            <Search size={11} className="text-neutral-400" />
            <input
              data-testid="sidebar-project-search"
              placeholder="Search projects…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent outline-none text-[13px] min-w-0"
            />
          </div>
          <Tip label="Create a new project — pick which vertical/category to place it in">
            <button
              onClick={() => setModal({ kind: "add-project", context: { folder_id: tree.folders[0]?.id || "", group_id: "" } })}
              className="p-1.5 rounded-md text-blue-600 hover:bg-neutral-100 transition-colors"
              data-testid="sidebar-new-project"
            >
              <Plus size={14} />
            </button>
          </Tip>
          <KebabMenu
            size={13}
            testid="sidebar-projects-kebab"
            items={[
              { label: "Add Business Vertical", icon: <FolderIcon size={13} />, onClick: () => setModal({ kind: "add-vertical" }), testid: "sidebar-kebab-add-vertical", description: "Create a new top-level workstream (e.g. Client Work)" },
            ]}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {/* All Projects row — always visible, shows tasks of all projects */}
        <button
          onClick={() => goto({ scope: "all" })}
          data-testid="sidebar-all-projects"
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm mb-1 transition-colors ${
            scope === "all" && !focus ? "fos-selected" : "text-neutral-700 hover:bg-neutral-100"
          }`}
        >
          <LayoutGrid size={13} />
          <span className="flex-1 text-left">All Projects</span>
          <span className="text-[10px] mono text-neutral-500">{tree.projects.length}</span>
        </button>

        {tree.folders.length === 0 ? (
          <div className="p-4 text-center">
            <FolderIcon size={20} className="mx-auto text-neutral-300 mb-2" />
            <p className="text-[11px] text-neutral-500 mb-2">No verticals yet.</p>
            <button
              onClick={() => setModal({ kind: "add-vertical" })}
              className="text-xs px-3 py-1.5 rounded-sm bg-neutral-900 text-white"
              data-testid="sidebar-empty-add-vertical"
            >
              + Add Business Vertical
            </button>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="folders" type="FOLDER">
              {(dp) => (
                <div ref={dp.innerRef} {...dp.droppableProps}>
                  {tree.folders.map((f, fIdx) => {
                    const open = expanded.has(f.id);
                    const groupsOfF = tree.groups.filter((g) => g.folder_id === f.id);
                    const projectsOfF = filteredProjects.filter((p) => p.folder_id === f.id && !p.group_id);
                    const vScopeActive = scope === `vertical:${f.id}`;
                    return (
                      <Draggable key={f.id} draggableId={`folder-${f.id}`} index={fIdx} isDragDisabled={dragDisabled}>
                        {(fp) => (
                          <div
                            ref={fp.innerRef}
                            {...fp.draggableProps}
                            className="mt-1"
                          >
                            {/* Vertical (folder) header row */}
                            <div
                              className={`group flex items-center gap-1.5 px-2 py-1.5 rounded-md cursor-pointer text-sm ${
                                vScopeActive ? "fos-selected" : "hover:bg-neutral-100"
                              }`}
                              data-testid={`sidebar-vertical-${f.id}`}
                            >
                              <button onClick={() => toggleFolder(f.id)} {...fp.dragHandleProps} className="p-0.5 shrink-0" aria-label={open ? "Collapse" : "Expand"}>
                                {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              </button>
                              <VerticalIcon name={f.icon} size={12} className="shrink-0" />
                              <span
                                className={`flex-1 truncate text-[13px] ${vScopeActive ? "" : "font-medium"}`}
                                onClick={() => goto({ scope: `vertical:${f.id}` })}
                              >{f.name}</span>
                              <div onClick={(e) => e.stopPropagation()}>
                                <KebabMenu
                                  size={12}
                                  testid={`sidebar-vertical-kebab-${f.id}`}
                                  items={[
                                    { label: "Add Category", icon: <Layers size={12} />, onClick: () => setModal({ kind: "add-category", context: { folder_id: f.id } }), description: "Add a sub-group under this vertical (e.g. At Risk)" },
                                    { label: "Add Project", icon: <Plus size={12} />, onClick: () => setModal({ kind: "add-project", context: { folder_id: f.id, group_id: "" } }), description: "Create a project directly under this vertical" },
                                  ]}
                                />
                              </div>
                            </div>

                            {/* Body — vertical guide line aligns with the
                                chevron center; content sits to the right of
                                the line. Categories are visually grouped with
                                extra vertical spacing (Notion / Linear style). */}
                            {open && (
                              <div className="mt-1 ml-3.5 pl-3 border-l border-neutral-300 pb-1 space-y-3">
                                {groupsOfF.map((g) => {
                                  const gsProjects = filteredProjects.filter((p) => p.group_id === g.id);
                                  const cScopeActive = scope === `category:${g.id}`;
                                  return (
                                    <div key={g.id}>
                                      <div className={`group flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer ${cScopeActive ? "fos-selected" : "hover:bg-neutral-100"}`}>
                                        <span
                                          className="flex-1 uppercase font-mono text-[10px] text-neutral-500 tracking-wider truncate"
                                          onClick={() => goto({ scope: `category:${g.id}` })}
                                        >{g.name}</span>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setModal({ kind: "add-project", context: { folder_id: f.id, group_id: g.id } }); }}
                                          className="opacity-0 group-hover:opacity-100 text-xs fos-inline-link inline-flex items-center gap-0.5"
                                          data-testid={`sidebar-add-project-category-${g.id}`}
                                        ><Plus size={10} /> Project</button>
                                      </div>
                                      <Droppable droppableId={`proj-group-${g.id}`} type="PROJECT">
                                        {(gp) => (
                                          <div ref={gp.innerRef} {...gp.droppableProps} className="mt-1 space-y-0.5">
                                            {gsProjects.map((p, idx) => (
                                              <Draggable key={p.id} draggableId={`project-${p.id}`} index={idx} isDragDisabled={dragDisabled}>
                                                {(pp) => (
                                                  <div ref={pp.innerRef} {...pp.draggableProps} {...pp.dragHandleProps}>
                                                    <SidebarProjectRow p={p} active={focus === p.id} onClick={() => goto({ focus: p.id, scope: `project:${p.id}` })} />
                                                  </div>
                                                )}
                                              </Draggable>
                                            ))}
                                            {gp.placeholder}
                                          </div>
                                        )}
                                      </Droppable>
                                    </div>
                                  );
                                })}

                                {/* Projects that have no category (belong directly to vertical) */}
                                <Droppable droppableId={`proj-folder-${f.id}`} type="PROJECT">
                                  {(fpp) => (
                                    <div ref={fpp.innerRef} {...fpp.droppableProps} className="space-y-0.5">
                                      {projectsOfF.map((p, idx) => (
                                        <Draggable key={p.id} draggableId={`project-${p.id}`} index={idx} isDragDisabled={dragDisabled}>
                                          {(pp) => (
                                            <div ref={pp.innerRef} {...pp.draggableProps} {...pp.dragHandleProps}>
                                              <SidebarProjectRow p={p} active={focus === p.id} onClick={() => goto({ focus: p.id, scope: `project:${p.id}` })} />
                                            </div>
                                          )}
                                        </Draggable>
                                      ))}
                                      {fpp.placeholder}
                                    </div>
                                  )}
                                </Droppable>

                                {/* Inline links — larger tap target, sit below with breathing room */}
                                <div className="flex items-center gap-5 pt-1 px-2">
                                  <button
                                    onClick={() => setModal({ kind: "add-category", context: { folder_id: f.id } })}
                                    className="text-xs fos-inline-link inline-flex items-center gap-1"
                                    data-testid={`sidebar-add-category-vertical-${f.id}`}
                                  ><Plus size={11} /> Category</button>
                                  <button
                                    onClick={() => setModal({ kind: "add-project", context: { folder_id: f.id, group_id: "" } })}
                                    className="text-xs fos-inline-link inline-flex items-center gap-1"
                                    data-testid={`sidebar-add-project-vertical-${f.id}`}
                                  ><Plus size={11} /> Project</button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {dp.placeholder}
                  <button
                    onClick={() => setModal({ kind: "add-vertical" })}
                    className="text-xs fos-inline-link mt-3 mb-1 pl-2 inline-flex items-center gap-1"
                    data-testid="sidebar-add-vertical-inline"
                  ><Plus size={11} /> Add Business Vertical</button>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {modal?.kind === "add-vertical" && (
        <VerticalCategoryModal kind="vertical" onClose={() => setModal(null)} onCreated={async () => { setModal(null); await reload(); }} />
      )}
      {modal?.kind === "add-category" && (
        <VerticalCategoryModal kind="category" verticals={tree.folders} initialVerticalId={modal.context?.folder_id}
          onClose={() => setModal(null)} onCreated={async () => { setModal(null); await reload(); }} />
      )}
      {modal?.kind === "add-project" && (
        <NewProjectModal verticals={tree.folders} categories={tree.groups}
          initialVerticalId={modal.context?.folder_id}
          initialCategoryId={modal.context?.group_id || ""}
          onClose={() => setModal(null)}
          onCreated={async (createdId) => { setModal(null); await reload(); if (createdId) goto({ focus: createdId, scope: `project:${createdId}` }); }} />
      )}
    </div>
  );
}

function SidebarProjectRow({ p, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`sidebar-project-${p.id}`}
      className={`w-full flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] transition-colors ${
        active ? "fos-selected" : "text-neutral-700 hover:bg-neutral-100"
      }`}
    >
      <StatusDot status={p.status} />
      <span className="flex-1 truncate text-left">{p.name}</span>
    </button>
  );
}

// ============================================================================
// Modals
// ============================================================================
function VerticalCategoryModal({ kind, verticals = [], initialVerticalId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("folder");
  const [verticalId, setVerticalId] = useState(initialVerticalId || verticals[0]?.id || "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (kind === "vertical") await endpoints.createFolder({ name, icon });
      else {
        if (!verticalId) return toast.error("Pick a Business Vertical");
        await endpoints.createGroup({ name, folder_id: verticalId });
      }
      toast.success(kind === "vertical" ? "Business Vertical created" : "Category created");
      onCreated();
    } catch (e) { toast.error("Could not create"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="fos-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid={`modal-add-${kind}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">{kind === "vertical" ? "New Business Vertical" : "New Category"}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        {kind === "category" && (
          <>
            <p className="text-xs text-neutral-500 mb-1">Business Vertical</p>
            <select value={verticalId} onChange={(e) => setVerticalId(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white mb-3"
              data-testid="modal-category-vertical">
              {verticals.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </>
        )}
        <p className="text-xs text-neutral-500 mb-1">Name</p>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder={kind === "vertical" ? "e.g. Client Work" : "e.g. Active Clients"}
          className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500 mb-3"
          data-testid={`modal-${kind}-name`} />
        {kind === "vertical" && (
          <>
            <p className="text-xs text-neutral-500 mb-1">Icon</p>
            <div className="flex gap-2 mb-3">
              {Object.keys(ICON_MAP).map((k) => (
                <button key={k} onClick={() => setIcon(k)} type="button"
                  className={`p-2 rounded border ${icon === k ? "border-neutral-900 bg-neutral-100" : "border-neutral-200"}`}
                  data-testid={`icon-${k}`}>
                  <VerticalIcon name={k} size={14} />
                </button>
              ))}
            </div>
          </>
        )}
        <button onClick={submit} disabled={saving || !name.trim()}
          className="w-full text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
          data-testid={`modal-${kind}-submit`}>Create</button>
      </div>
    </div>
  );
}

export function NewProjectModal({ verticals, categories, initialVerticalId, initialCategoryId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [verticalId, setVerticalId] = useState(initialVerticalId || verticals[0]?.id || "");
  const [categoryId, setCategoryId] = useState(initialCategoryId || "");
  const [creatingV, setCreatingV] = useState(false);
  const [newV, setNewV] = useState("");
  const [creatingC, setCreatingC] = useState(false);
  const [newC, setNewC] = useState("");
  const [saving, setSaving] = useState(false);
  const [localVerticals, setLocalVerticals] = useState(verticals);
  const [localCategories, setLocalCategories] = useState(categories);
  useEffect(() => setLocalVerticals(verticals), [verticals]);
  useEffect(() => setLocalCategories(categories), [categories]);
  const verticalCategories = localCategories.filter((c) => c.folder_id === verticalId);

  const doCreateVertical = async () => {
    if (!newV.trim()) return;
    const f = await endpoints.createFolder({ name: newV });
    setLocalVerticals([...localVerticals, f]);
    setVerticalId(f.id);
    setNewV(""); setCreatingV(false);
    toast.success("Business Vertical created");
  };
  const doCreateCategory = async () => {
    if (!newC.trim() || !verticalId) return;
    const g = await endpoints.createGroup({ name: newC, folder_id: verticalId });
    setLocalCategories([...localCategories, g]);
    setCategoryId(g.id);
    setNewC(""); setCreatingC(false);
    toast.success("Category created");
  };
  const submit = async () => {
    if (!name.trim()) return;
    if (!verticalId) return toast.error("Business Vertical required");
    setSaving(true);
    try {
      const created = await endpoints.createProject({
        name, folder_id: verticalId, group_id: categoryId || null, status: "active", health_score: 80,
      });
      toast.success("Project created");
      onCreated(created?.id);
    } catch (e) { toast.error("Could not create project"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="fos-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="modal-new-project">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">New Project</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <p className="text-xs text-neutral-500 mb-1">Project name</p>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Project name"
          className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500 mb-3"
          data-testid="new-project-name" />
        <p className="text-xs text-neutral-500 mb-1">Business Vertical</p>
        {!creatingV ? (
          <div className="flex items-center gap-2 mb-3">
            <select value={verticalId} onChange={(e) => {
              if (e.target.value === "__new__") { setCreatingV(true); return; }
              setVerticalId(e.target.value); setCategoryId("");
            }}
              className="flex-1 text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none"
              data-testid="new-project-vertical">
              {localVerticals.length === 0 && <option value="">Select…</option>}
              {localVerticals.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              <option value="__new__">+ Create new Business Vertical…</option>
            </select>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-3">
            <input value={newV} onChange={(e) => setNewV(e.target.value)} autoFocus
              onKeyDown={(e) => e.key === "Enter" && doCreateVertical()}
              placeholder="New vertical name"
              className="flex-1 text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white"
              data-testid="new-project-vertical-input" />
            <button onClick={doCreateVertical} className="text-xs px-2 py-2 rounded-sm bg-neutral-900 text-white" data-testid="new-project-vertical-save">Save</button>
            <button onClick={() => { setCreatingV(false); setNewV(""); }} className="text-xs px-2 py-2 rounded-sm border border-neutral-200">Cancel</button>
          </div>
        )}
        <p className="text-xs text-neutral-500 mb-1">Category <span className="text-neutral-400">(optional)</span></p>
        {!creatingC ? (
          <div className="flex items-center gap-2 mb-4">
            <select value={categoryId} onChange={(e) => {
              if (e.target.value === "__new__") { setCreatingC(true); return; }
              setCategoryId(e.target.value);
            }}
              className="flex-1 text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none"
              data-testid="new-project-category"
              disabled={!verticalId}>
              <option value="">— None (directly in Vertical)</option>
              {verticalCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              {verticalId && <option value="__new__">+ Create new Category…</option>}
            </select>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-4">
            <input value={newC} onChange={(e) => setNewC(e.target.value)} autoFocus
              onKeyDown={(e) => e.key === "Enter" && doCreateCategory()}
              placeholder="New category name"
              className="flex-1 text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white"
              data-testid="new-project-category-input" />
            <button onClick={doCreateCategory} className="text-xs px-2 py-2 rounded-sm bg-neutral-900 text-white" data-testid="new-project-category-save">Save</button>
            <button onClick={() => { setCreatingC(false); setNewC(""); }} className="text-xs px-2 py-2 rounded-sm border border-neutral-200">Cancel</button>
          </div>
        )}
        <button onClick={submit} disabled={saving || !name.trim() || !verticalId}
          className="w-full text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
          data-testid="new-project-submit">Create Project</button>
      </div>
    </div>
  );
}
